import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { SPLIT_TYPE, getSplitTypeLabel, type ExpenseSplitView } from "@/lib/types";
import { loadNewExpense, createBulkExpensesFn } from "@/lib/server-fns/expenses";
import {
  Container,
  Heading,
  Button,
  Input,
  HStack,
  Card,
  Text,
  VStack,
  NativeSelect,
  Field,
  Flex,
  SegmentGroup,
  For,
  IconButton,
  Box,
  Grid,
  Collapsible,
} from "@chakra-ui/react";
import { showErrorToast } from "@/lib/toast-utils";
import { buildParticipantOptions, getDisplayName } from "@/lib/user-utils";
import { CURRENCY_ICONS, SUPPORTED_CURRENCIES } from "@/lib/constants";
import { LuPlus, LuArrowLeft, LuTrash2, LuChevronDown, LuEraser } from "react-icons/lu";
import { useState, useRef, useCallback } from "react";
import { SplitParticipants } from "@/components/split-participants";
import { InputGroup } from "@/components/ui/input-group";
import { Alert } from "@/components/ui/alert";
import { CloseButton } from "@/components/ui/close-button";
import { FileUploadDropzone, FileUploadRoot } from "@/components/ui/file-upload";
import { parseTransactionCsv, type SkippedCsvRow } from "@/lib/csv-import";
import type { FileUploadFileAcceptDetails, FileUploadFileRejectDetails } from "@chakra-ui/react";

export const Route = createFileRoute("/_authenticated/expenses/bulk")({
  loader: () => loadNewExpense(),
  component: BulkExpenses,
});

interface BulkRow {
  id: string;
  date: string;
  description: string;
  amount: string;
}

interface ImportSummary {
  fileName: string;
  imported: number;
  skipped: SkippedCsvRow[];
  signFlipped: boolean;
}

/** Refuse absurdly large files before we read them into memory. */
const MAX_CSV_BYTES = 2 * 1024 * 1024;

const REJECT_MESSAGES: Record<string, string> = {
  FILE_INVALID_TYPE: "Only .csv files can be imported",
  FILE_TOO_LARGE: "The file is larger than 2 MB",
  TOO_MANY_FILES: "Import one file at a time",
};

function createEmptyRow(): BulkRow {
  return {
    id: crypto.randomUUID(),
    date: new Date().toISOString().split("T")[0],
    description: "",
    amount: "",
  };
}

function isEmptyRow(row: BulkRow): boolean {
  return !row.description && !row.amount;
}

function BulkExpenses() {
  const { friends, currentUser, defaultCurrency } = Route.useLoaderData();
  const navigate = useNavigate();
  const [splitType, setSplitType] = useState<string>(SPLIT_TYPE.EQUAL);
  const [splits, setSplits] = useState<ExpenseSplitView[]>([
    { userId: currentUser.id, amount: "" },
  ]);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [paidBy, setPaidBy] = useState(currentUser.id);
  const [rows, setRows] = useState<BulkRow[]>([
    createEmptyRow(),
    createEmptyRow(),
    createEmptyRow(),
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);

  const dateRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const participantOptions = buildParticipantOptions({ currentUser, friends });

  const handleFileAccept = useCallback(async (details: FileUploadFileAcceptDetails) => {
    const file = details.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const { rows: parsed, skipped, signFlipped } = parseTransactionCsv(await file.text());
      setImportSummary({
        fileName: file.name,
        imported: parsed.length,
        skipped,
        signFlipped,
      });

      // The summary alert already explains why nothing came through, so there
      // is no toast for this case.
      if (parsed.length === 0) return;

      // Keep whatever the user already typed, drop the untouched blank rows.
      setRows((prev) => [
        ...prev.filter((row) => !isEmptyRow(row)),
        ...parsed.map((row) => ({ id: crypto.randomUUID(), ...row })),
      ]);
    } catch (error) {
      showErrorToast("Could not import CSV", error);
    } finally {
      setImporting(false);
    }
  }, []);

  const handleFileReject = useCallback((details: FileUploadFileRejectDetails) => {
    const code = details.files[0]?.errors[0];
    showErrorToast(
      "Could not import CSV",
      new Error((code && REJECT_MESSAGES[code]) ?? "The file could not be read"),
    );
  }, []);

  const clearRows = useCallback(() => {
    setRows([createEmptyRow()]);
    setImportSummary(null);
  }, []);

  const addRow = useCallback(() => {
    const newRow = createEmptyRow();
    setRows((prev) => [...prev, newRow]);
    requestAnimationFrame(() => {
      const el = dateRefs.current.get(newRow.id);
      if (el) el.focus();
    });
  }, []);

  const updateRow = useCallback((id: string, field: keyof BulkRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  }, []);

  const deleteRow = useCallback(
    (id: string) => {
      if (rows.length <= 1) return;
      setRows((prev) => prev.filter((r) => r.id !== id));
    },
    [rows.length],
  );

  const handleAmountKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, rowId: string) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      const idx = rows.findIndex((r) => r.id === rowId);
      if (idx === rows.length - 1) {
        addRow();
      } else {
        const nextRow = rows[idx + 1];
        const el = dateRefs.current.get(nextRow.id);
        if (el) el.focus();
      }
    },
    [rows, addRow],
  );

  const handleSubmit = async () => {
    // Filter to rows that have at least some content
    const filledRows = rows.filter((r) => r.description || r.amount);

    if (filledRows.length === 0) {
      showErrorToast("No expenses to create", new Error("Enter at least one expense row"));
      return;
    }

    // Validate: rows with partial data
    const incomplete = filledRows.filter((r) => !r.description || !r.amount || !r.date);
    if (incomplete.length > 0) {
      showErrorToast(
        "Incomplete rows",
        new Error("Each row must have a date, description, and amount"),
      );
      return;
    }

    // Validate amount values
    const invalidAmount = filledRows.find(
      (r) => isNaN(parseFloat(r.amount)) || parseFloat(r.amount) <= 0,
    );
    if (invalidAmount) {
      showErrorToast(
        "Invalid amount",
        new Error(`"${invalidAmount.description}" has an invalid amount`),
      );
      return;
    }

    setSubmitting(true);
    try {
      await createBulkExpensesFn({
        data: {
          rows: filledRows.map((r) => ({
            description: r.description,
            amount: r.amount,
            date: new Date(r.date).toISOString().split("T")[0],
          })),
          currency,
          paidBy,
          splitType,
          splits,
        },
      });
      navigate({ to: "/expenses" });
    } catch (error) {
      showErrorToast("Failed to create expenses", error);
    } finally {
      setSubmitting(false);
    }
  };

  const filledCount = rows.filter((r) => r.description && r.amount).length;

  return (
    <Container maxW="4xl" px={{ base: "0", md: "4" }}>
      <HStack mb="6" px={{ base: "4", md: "0" }}>
        <Button asChild variant="ghost">
          <Link to="/expenses">
            <LuArrowLeft size={16} />
            Back to Expenses
          </Link>
        </Button>
      </HStack>

      {/* Collapsible settings */}
      <Collapsible.Root mb="4">
        <Collapsible.Trigger asChild>
          <Button variant="outline" size="lg" w="full" justifyContent="space-between">
            <Text fontSize="sm">
              {currency} · {getDisplayName(paidBy, { currentUser, friends })} paid ·{" "}
              {getSplitTypeLabel(splitType)}
            </Text>
            <Collapsible.Indicator
              transition="transform 0.2s"
              _open={{ transform: "rotate(180deg)" }}
            >
              <LuChevronDown />
            </Collapsible.Indicator>
          </Button>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Card.Root variant="outline" mt="2">
            <Card.Body p={{ base: "4", md: "6" }}>
              <VStack gap="4" align="stretch">
                <Field.Root required>
                  <Field.Label>Currency</Field.Label>
                  <SegmentGroup.Root
                    value={currency}
                    onValueChange={(details) => setCurrency(details.value ?? defaultCurrency)}
                    width="full"
                    css={{
                      "--segment-indicator-bg": "colors.loonie.solid",
                      "--segment-indicator-shadow": "shadows.md",
                    }}
                  >
                    <SegmentGroup.Indicator />
                    <For each={SUPPORTED_CURRENCIES}>
                      {(code) => (
                        <SegmentGroup.Item key={code} value={code}>
                          <SegmentGroup.ItemText>
                            <HStack gap="1.5">
                              {CURRENCY_ICONS[code]}
                              {code}
                            </HStack>
                          </SegmentGroup.ItemText>
                          <SegmentGroup.ItemHiddenInput />
                        </SegmentGroup.Item>
                      )}
                    </For>
                  </SegmentGroup.Root>
                </Field.Root>

                <Field.Root required>
                  <Field.Label>Paid By</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field value={paidBy} onChange={(e) => setPaidBy(e.target.value)}>
                      {participantOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Field.Root>
                  <Field.Label>Split Type</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={splitType}
                      onChange={(e) => setSplitType(e.target.value)}
                    >
                      <option value={SPLIT_TYPE.EQUAL}>Split Equally</option>
                      <option value={SPLIT_TYPE.PERCENTAGE}>By Percentage</option>
                      <option value={SPLIT_TYPE.SHARES}>By Shares</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </VStack>
            </Card.Body>
          </Card.Root>
        </Collapsible.Content>
      </Collapsible.Root>

      {/* Participants (always visible) */}
      <Card.Root variant="outline" mb="4">
        <Card.Body p={{ base: "4", md: "6" }}>
          <Text fontSize="sm" fontWeight="medium" mb="2">
            Split With
          </Text>
          <SplitParticipants
            currentUser={currentUser}
            friends={friends}
            splits={splits}
            splitType={splitType}
            onSplitsChange={setSplits}
          />
        </Card.Body>
      </Card.Root>

      {/* CSV import */}
      <Card.Root variant="outline" mb="4">
        <Card.Body p={{ base: "4", md: "6" }}>
          <Heading as="h3" size="md" mb="1">
            Import from CSV
          </Heading>
          <Text fontSize="sm" color="fg.muted" mb="4">
            Drop a bank or budgeting-app export to fill the table below. Only the date, description
            and amount columns are used — review and edit the rows before saving.
          </Text>

          <FileUploadRoot
            accept={[".csv", "text/csv"]}
            maxFiles={1}
            // Held empty so dropping the same file again re-imports it.
            acceptedFiles={[]}
            maxFileSize={MAX_CSV_BYTES}
            onFileAccept={handleFileAccept}
            onFileReject={handleFileReject}
            disabled={importing}
          >
            <FileUploadDropzone
              label={importing ? "Reading file…" : "Drop a CSV here or click to browse"}
              description="Up to 2 MB"
            />
          </FileUploadRoot>

          {importSummary && (
            <Alert
              mt="4"
              status={importSummary.imported > 0 ? "info" : "warning"}
              title={`Imported ${importSummary.imported} row${
                importSummary.imported !== 1 ? "s" : ""
              } from ${importSummary.fileName}`}
              endElement={
                <CloseButton
                  size="xs"
                  aria-label="Dismiss import summary"
                  onClick={() => setImportSummary(null)}
                />
              }
            >
              <VStack align="start" gap="1">
                {importSummary.signFlipped && (
                  <Text>Amounts were negative for spending, so the signs were flipped.</Text>
                )}
                {importSummary.skipped.length > 0 && (
                  <>
                    <Text>
                      Skipped {importSummary.skipped.length} row
                      {importSummary.skipped.length !== 1 ? "s" : ""}:
                    </Text>
                    <VStack as="ul" align="start" gap="0.5" pl="4">
                      {importSummary.skipped.slice(0, 5).map((row) => (
                        <Text as="li" key={row.line} listStyleType="disc">
                          {row.description} — {row.reason}
                        </Text>
                      ))}
                      {importSummary.skipped.length > 5 && (
                        <Text as="li" listStyleType="disc">
                          and {importSummary.skipped.length - 5} more
                        </Text>
                      )}
                    </VStack>
                  </>
                )}
              </VStack>
            </Alert>
          )}
        </Card.Body>
      </Card.Root>

      {/* Expense rows card */}
      <Card.Root variant="outline" mb="4">
        <Card.Body p={{ base: "4", md: "6" }}>
          <Heading as="h3" size="md" mb="4">
            Expenses
          </Heading>

          {/* Desktop header */}
          <Grid
            display={{ base: "none", md: "grid" }}
            templateColumns="150px 1fr 150px 40px"
            gap="3"
            mb="2"
            px="1"
          >
            <Text fontSize="sm" fontWeight="medium" color="fg.muted">
              Date
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="fg.muted">
              Description
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="fg.muted">
              Amount
            </Text>
            <Box />
          </Grid>

          <VStack gap="3" align="stretch">
            {rows.map((row) => (
              <Box key={row.id}>
                {/* Desktop: inline row */}
                <Grid
                  display={{ base: "none", md: "grid" }}
                  templateColumns="150px 1fr 150px 40px"
                  gap="3"
                  alignItems="center"
                >
                  <Input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateRow(row.id, "date", e.target.value)}
                    ref={(el) => {
                      if (el) dateRefs.current.set(row.id, el);
                      else dateRefs.current.delete(row.id);
                    }}
                  />
                  <Input
                    placeholder="Description"
                    value={row.description}
                    onChange={(e) => updateRow(row.id, "description", e.target.value)}
                  />
                  <InputGroup startElement={CURRENCY_ICONS[currency]}>
                    <Input
                      placeholder="0.00"
                      type="number"
                      min={0.01}
                      step={0.01}
                      value={row.amount}
                      onChange={(e) => updateRow(row.id, "amount", e.target.value)}
                      onKeyDown={(e) => handleAmountKeyDown(e, row.id)}
                    />
                  </InputGroup>
                  <IconButton
                    aria-label="Remove row"
                    variant="ghost"
                    colorPalette="red"
                    size="sm"
                    onClick={() => deleteRow(row.id)}
                    disabled={rows.length <= 1}
                  >
                    <LuTrash2 size={16} />
                  </IconButton>
                </Grid>

                {/* Mobile: stacked fields */}
                <VStack display={{ base: "flex", md: "none" }} gap="2" align="stretch">
                  <Flex gap="2">
                    <Input
                      type="date"
                      value={row.date}
                      onChange={(e) => updateRow(row.id, "date", e.target.value)}
                      flex="1"
                      ref={(el) => {
                        // Desktop ref takes precedence; mobile sets if not already set
                        if (el && !dateRefs.current.has(row.id)) {
                          dateRefs.current.set(row.id, el);
                        }
                      }}
                    />
                    <IconButton
                      aria-label="Remove row"
                      variant="ghost"
                      colorPalette="red"
                      size="sm"
                      onClick={() => deleteRow(row.id)}
                      disabled={rows.length <= 1}
                      flexShrink={0}
                    >
                      <LuTrash2 size={16} />
                    </IconButton>
                  </Flex>
                  <Input
                    placeholder="Description"
                    value={row.description}
                    onChange={(e) => updateRow(row.id, "description", e.target.value)}
                  />
                  <InputGroup startElement={CURRENCY_ICONS[currency]}>
                    <Input
                      placeholder="0.00"
                      type="number"
                      min={0.01}
                      step={0.01}
                      value={row.amount}
                      onChange={(e) => updateRow(row.id, "amount", e.target.value)}
                      onKeyDown={(e) => handleAmountKeyDown(e, row.id)}
                    />
                  </InputGroup>
                </VStack>
              </Box>
            ))}
          </VStack>

          <HStack mt="4" gap="2">
            <Button variant="outline" size="sm" onClick={addRow}>
              <LuPlus size={16} />
              Add Row
            </Button>
            <Button
              variant="ghost"
              size="sm"
              colorPalette="red"
              onClick={clearRows}
              disabled={rows.every(isEmptyRow)}
            >
              <LuEraser size={16} />
              Clear All
            </Button>
          </HStack>
        </Card.Body>
      </Card.Root>

      {/* Footer */}
      <Flex
        direction={{ base: "column-reverse", md: "row" }}
        justify="flex-end"
        gap="3"
        px={{ base: "4", md: "0" }}
        mb="8"
      >
        <Button asChild variant="ghost" w={{ base: "full", md: "auto" }}>
          <Link to="/expenses">Cancel</Link>
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || filledCount === 0}
          loading={submitting}
          w={{ base: "full", md: "auto" }}
        >
          Create {filledCount} Expense{filledCount !== 1 ? "s" : ""}
        </Button>
      </Flex>
    </Container>
  );
}
